import { Trans, useLingui } from '@lingui/react/macro';

import AlertBar from '@/design_system/AlertBar';
import { Label } from '@/design_system/Label/Label';
import Message from '@/design_system/Message';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import Stack from '@/design_system/Stack';
import { useIsUpdatingWorkshop, useUpdateServiceChoice } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useCurrentOrganization } from '@/services/auth';
import useViewPort from '@/utils/useViewport';

export const ServiceChoice = () => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  const { article, view, errors, state } = useArticleContext();

  const isUpdatingWorkshop = useIsUpdatingWorkshop(article.id);
  const { mutateAsync: updateServiceChoice, isPending: isUpdateServiceChoicePending } =
    useUpdateServiceChoice({
      articleId: article.id,
      requestId: article.requestId,
    });

  const serviceOptions = view.services.choice.serviceOptions;

  if (!serviceOptions || (serviceOptions.length <= 1 && article.snapshot.serviceChoice)) {
    return null;
  }

  return (
    <>
      <Stack gap="0.5rem">
        {!isMobile && (
          <Label
            label={t({
              id: 'service-choice.radio.label',
              message: 'What service can be provided?',
            })}
          />
        )}
        <RadioBoxGroup
          name="service-choice"
          value={article.snapshot.serviceChoice}
          onChange={(value) => {
            state.errors.check(null);
            updateServiceChoice({
              serviceChoice: value,
            });
          }}
          disabled={
            !view.services.choice.editable || isUpdateServiceChoicePending || isUpdatingWorkshop
          }
        >
          {serviceOptions.includes('care-repair') && (
            <RadioBox
              value="care-repair"
              ariaLabel={t({
                id: 'service-choice.radio.care-repair.label',
                message: 'Care and repair',
              })}
              title={<Trans id="service-choice.radio.care-repair.label">Care and repair</Trans>}
            >
              <Trans id="service-choice.radio.care-repair.sub-text">
                Address and fix the item's defect
              </Trans>
            </RadioBox>
          )}
          {serviceOptions.includes('exchange') && (
            <RadioBox
              value="exchange"
              ariaLabel={t({
                id: 'service-choice.radio.exchange.label',
                message: 'Exchange',
              })}
              title={<Trans id="service-choice.radio.exchange.label">Exchange</Trans>}
            >
              <Trans id="service-choice.radio.exchange.sub-text">
                Provide a new or equivalent item
              </Trans>
            </RadioBox>
          )}
          {serviceOptions.includes('exchange-archival') && (
            <RadioBox
              value="exchange-archival"
              ariaLabel={t({
                id: 'service-choice.radio.exchange-archival.label',
                message: 'Exchange',
              })}
              title={<Trans id="service-choice.radio.exchange-archival.label">Exchange</Trans>}
            >
              <Trans id="service-choice.radio.exchange-archival.sub-text">
                Provide a new or equivalent item
              </Trans>
            </RadioBox>
          )}
          {serviceOptions.includes('refund') && (
            <RadioBox
              value="refund"
              ariaLabel={t({
                id: 'service-choice.radio.refund.label',
                message: 'Refund',
              })}
              title={<Trans id="service-choice.radio.refund.label">Refund</Trans>}
            >
              <Trans id="service-choice.radio.refund.sub-text">
                Issue a full or partial refund
              </Trans>
            </RadioBox>
          )}
          {serviceOptions.includes('none') && (
            <RadioBox
              value="none"
              ariaLabel={t({
                id: 'service-choice.radio.none.label',
                message: 'None',
              })}
              title={<Trans id="service-choice.radio.none.label">None</Trans>}
            >
              <Trans id="service-choice.radio.none.sub-text">No service can be provided</Trans>
            </RadioBox>
          )}
        </RadioBoxGroup>
        {errors.services?.choice?.noChoice && (
          <Message type="error">
            <Trans id="service-choice.radio.error.no-choice">Please select a service type.</Trans>
          </Message>
        )}
      </Stack>
      {(article.snapshot.serviceChoice === 'exchange-archival' ||
        article.snapshot.serviceChoice === 'refund') && <ServiceExchangeArchivalRefundMessage />}
      {article.snapshot.serviceChoice === 'none' && <ServiceNoneMessage />}
    </>
  );
};

const ServiceNoneMessage = () => {
  const { request, workflow } = useRequestContext();
  const { article } = useArticleContext();

  const isClientNotificationsEnabled =
    !!request.organization.config.clientCommunication?.notifications?.enabled;
  const isServiceChoiceCommentEnabled =
    !!request.organization.config.clientCommunication?.serviceChoiceComment?.enabled;

  const currentNode = workflow?.nodes.find((node) => node.id === article.step?.id);

  if (!currentNode || !('step' in currentNode) || currentNode.step !== 'service-choice') {
    return null;
  }

  const nextNodeIfChoiceNone = workflow!.nodes.find((node) => node.id === currentNode.next.none);

  if (!nextNodeIfChoiceNone || !('step' in nextNodeIfChoiceNone)) {
    if (request.client && isServiceChoiceCommentEnabled) {
      return (
        <AlertBar type="info">
          <Trans id="service-choice.none.message.archival.comment">
            You can add a comment for the client in the section below.
          </Trans>
        </AlertBar>
      );
    } else {
      return null;
    }
  }

  const nextStep = nextNodeIfChoiceNone;

  if (nextStep?.step === 'transit') {
    if (nextStep.config.destinationType === 'client' && isClientNotificationsEnabled) {
      if (nextStep.config.clientPickup) {
        return (
          <AlertBar type="info">
            <Trans id="service-choice.none.message.transit.store-client">
              The client will be informed to come pick their item up at the store.
            </Trans>
            {isServiceChoiceCommentEnabled && (
              <>
                {' '}
                <Trans id="service-choice.none.message.transit.store-client.comment">
                  You can add a comment for them in the section below.
                </Trans>
              </>
            )}
          </AlertBar>
        );
      } else {
        return (
          <AlertBar type="info">
            <Trans id="service-choice.none.message.transit.workshop-client">
              The item will be sent back to the client.
            </Trans>
            {isServiceChoiceCommentEnabled && (
              <>
                {' '}
                <Trans id="service-choice.none.message.transit.workshop-client.comment">
                  You can add a comment for them in the section below.
                </Trans>
              </>
            )}
          </AlertBar>
        );
      }
    }

    if (nextStep.config.originType === 'workshop' && nextStep.config.destinationType === 'store') {
      return (
        <AlertBar type="info">
          <Trans id="service-choice.none.message.transit.workshop-store">
            The item will be sent back to the store.
          </Trans>{' '}
          {request.client && isServiceChoiceCommentEnabled && (
            <Trans id="service-choice.none.message.transit.workshop-store.comment">
              You can add a comment for the client in the section below.
            </Trans>
          )}
        </AlertBar>
      );
    }
  }

  if (nextStep?.step === 'archival') {
    return (
      <AlertBar type="info">
        {request.client && isServiceChoiceCommentEnabled && (
          <Trans id="service-choice.none.message.archival.comment">
            You can add a comment for the client in the section below.
          </Trans>
        )}{' '}
        {request.articles.length === 1 ? (
          <Trans id="service-choice.none.message.archival.item-and-request">
            The item and the request will be automatically archived.
          </Trans>
        ) : (
          <Trans id="service-choice.none.message.archival.item">
            The item will be automatically archived.
          </Trans>
        )}
      </AlertBar>
    );
  }

  return null;
};

const ServiceExchangeArchivalRefundMessage = () => {
  const { request, article } = useArticleContext();
  const [organization] = useCurrentOrganization();

  return organization?.config.integrations?.zendesk ? (
    <ServiceExchangeArchivalRefundZendeskMessage />
  ) : (
    <AlertBar type="warning">
      {article.snapshot.serviceChoice === 'exchange-archival' ? (
        <Trans id="service-choice.exchange-archival.message.not-available">
          The exchange option through Prolong is not yet activated.
        </Trans>
      ) : (
        <Trans id="service-choice.refund.message.not-available">
          The refund option through Prolong is not yet activated.
        </Trans>
      )}{' '}
      {request.articles.length === 1 ? (
        <Trans id="service-choice.exchange-archival-refund.message.archival.request-and-item">
          Validating this diagnosis will archive the request and the item in Prolong.
        </Trans>
      ) : (
        <Trans id="service-choice.exchange-archival-refund.message.archival.item">
          Validating this diagnosis will archive the item in Prolong.
        </Trans>
      )}
    </AlertBar>
  );
};

const ServiceExchangeArchivalRefundZendeskMessage = () => {
  const { request, article } = useArticleContext();

  return (
    <AlertBar type="warning">
      {article.snapshot.serviceChoice === 'exchange-archival' ? (
        <Trans id="service-choice.exchange-archival.message.not-available.zendesk">
          The exchange option through Prolong is not yet activated. However, you can validate this
          diagnosis and send the item and request information to Zendesk.
        </Trans>
      ) : (
        <Trans id="service-choice.refund.message.not-available.zendesk">
          The refund option through Prolong is not yet activated. However, you can validate this
          diagnosis and send the item and request information to Zendesk.
        </Trans>
      )}{' '}
      {request.articles.length === 1 ? (
        <Trans id="service-choice.exchange-archival-refund.message.archival.request-and-item.zendesk">
          This will also automatically archive the request and the item in Prolong.
        </Trans>
      ) : (
        <Trans id="service-choice.exchange-archival-refund.message.archival.item.zendesk">
          This will also automatically archive the item in Prolong.
        </Trans>
      )}
    </AlertBar>
  );
};
